import React from "react";
import { Paper } from "@mui/material";

/**
 * Thin wrapper around MUI Paper.
 *
 * Props:
 *   padding:  "none" | "sm" | "md" | "lg" (default "md")
 *   variant:  "default" | "elevated" | "outlined" (default "default")
 *   hoverable: boolean — adds subtle lift + orange tint on hover
 */

const PADDING_MAP = {
  none: 0,
  sm: 2, // 16px
  md: 3, // 24px
  lg: 4, // 32px
};

const VARIANT_SX = {
  default: {
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "none",
  },
  elevated: {
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  },
  outlined: {
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "none",
  },
};

const HOVER_SX = {
  transition: "all 0.2s ease-out",
  "&:hover": {
    borderColor: "rgba(249, 115, 22, 0.3)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 20px rgba(249, 115, 22, 0.08)",
  },
};

const Card = React.forwardRef(function Card(
  {
    padding = "md",
    variant = "default",
    hoverable = false,
    children,
    sx,
    ...rest
  },
  ref
) {
  const baseSx = VARIANT_SX[variant] || VARIANT_SX.default;
  const p = PADDING_MAP[padding] ?? PADDING_MAP.md;

  const mergedSx = {
    ...baseSx,
    p,
    ...(hoverable ? HOVER_SX : {}),
    ...(sx || {}),
  };

  return (
    <Paper ref={ref} elevation={0} sx={mergedSx} {...rest}>
      {children}
    </Paper>
  );
});

export default Card;
